<template>
    <div class="ink-ripple" v-if="mounted || !isDisabled">
        <div class="ink-ripple--ripple" :class="rippleClasses" :style="rippleStyles" ref="ripple"></div>
    </div>
</template>

<script>
    import { touchevents } from 'modernizr';
    import { scrollTop, scrollLeft } from 'util/dom/scroll-pos';
    import bemMixin from 'framework/mixins/bem';


    function getEventName(type) {
        if (type === 'start') {
            return touchevents ? 'touchstart' : 'mousedown';
        }

        return touchevents ? 'touchend' : 'mouseup';
    }

    const addEvent = (target, type, handler) => {
        target.addEventListener(getEventName(type), handler, { passive: true });
    };

    const removeEvent = (target, type, handler) => {
        target.removeEventListener(getEventName(type), handler, { passive: true });
    };

    export default {
        mixins: [
            bemMixin('ink-ripple'),
        ],
        props: {
            disabled: Boolean,
        },
        data() {
            return {
                mounted: false,
                rippleElement: null,
                parentElement: null,
                parentDimensions: {
                    width: null,
                    height: null,
                    top: null,
                    left: null,
                },
                awaitingComplete: false,
                hasCompleted: false,
                fadeOut: false,
                active: false,
            };
        },
        computed: {
            rippleClasses() {
                return [
                    this.bemAdd(this.active ? 'is-active' : '', 'ripple'),
                    this.bemAdd(this.fadeOut ? 'is-fading' : '', 'ripple'),
                ];
            },
            rippleStyles() {
                return {
                    width: this.parentDimensions.width,
                    height: this.parentDimensions.height,
                    top: this.parentDimensions.top,
                    left: this.parentDimensions.left,
                };
            },
            isDisabled() {
                return this.disabled;
            },
        },
        methods: {
            checkAvailablePositions(element) {
                const availablePositions = ['relative', 'absolute', 'fixed'];
                return availablePositions.indexOf(getComputedStyle(element).position) > -1;
            },
            getClosestPositionedParent(element) {
                const parent = element && element.parentNode;

                if (!parent || parent.tagName.toLowerCase() === 'body') {
                    return false;
                }

                if (this.checkAvailablePositions(element)) {
                    return element;
                }

                return this.getClosestPositionedParent(parent);
            },
            getParentSize() {
                return `${ Math.round(Math.max(this.parentElement.offsetWidth, this.parentElement.offsetHeight)) }px`;
            },
            getClickPosition(event) {
                if (!this.$refs.ripple) {
                    return {};
                }

                const rect = this.parentElement.getBoundingClientRect();
                let top = event.pageY;
                let left = event.pageX;

                if (event.type === 'touchstart') {
                    top = event.changedTouches[0].pageY;
                    left = event.changedTouches[0].pageX;
                }

                return {
                    top: `${ top - rect.top - this.$refs.ripple.offsetHeight / 2 - scrollTop() }px`,
                    left: `${ left - rect.left - this.$refs.ripple.offsetWidth / 2 - scrollLeft() }px`,
                };
            },
            setDimensions() {
                const size = this.getParentSize();
                this.parentDimensions.width = size;
                this.parentDimensions.height = size;
            },
            setPositions(event) {
                const positions = this.getClickPosition(event);

                if (positions) {
                    this.parentDimensions.top = positions.top;
                    this.parentDimensions.left = positions.left;
                }
            },
            clearState() {
                this.active = false;
                this.fadeOut = false;
                this.hasCompleted = false;

                this.setDimensions();
                window.clearTimeout(this.awaitingComplete);
                removeEvent(document.body, 'end', this.endRipple);
            },
            startRipple(event) {
                this.clearState();

                this.awaitingComplete = window.setTimeout(() => {
                    this.hasCompleted = true;
                }, 400);

                addEvent(document.body, 'end', this.endRipple);

                this.$nextTick(() => {
                    this.setPositions(event);
                    this.active = true;
                });
            },
            endRipple() {
                if (this.hasCompleted) {
                    this.fadeOut = true;
                } else {
                    this.awaitingComplete = window.setTimeout(() => {
                        this.fadeOut = true;
                    }, 200);
                }

                removeEvent(document.body, 'end', this.endRipple);
            },
            registerTriggerEvent() {
                addEvent(this.parentElement, 'start', this.startRipple);
            },
            unregisterTriggerEvent() {
                if (!this.parentElement) {
                    return;
                }

                removeEvent(this.parentElement, 'start', this.startRipple);
            },
            init() {
                this.rippleElement = this.$el;
                this.parentElement = this.getClosestPositionedParent(this.$el.parentNode);

                if (this.parentElement) {
                    this.rippleElement.parentNode.removeChild(this.rippleElement);
                    this.parentElement.appendChild(this.rippleElement);
                    this.registerTriggerEvent();
                    this.setDimensions();
                } else {
                    this.$destroy();
                }
            },
            destroy() {
                if (this.rippleElement && this.rippleElement.parentNode) {
                    this.unregisterTriggerEvent();
                    this.rippleElement.parentNode.removeChild(this.rippleElement);
                }
            },
        },
        watch: {
            isDisabled: {
                handler() {
                    this[this.isDisabled ? 'destroy' : 'init']();
                },
            },
        },
        mounted() {
            window.setTimeout(() => {
                this[this.isDisabled ? 'destroy' : 'init']();

                this.$nextTick(() => {
                    this.mounted = true;
                });
            }, 100);
        },
        beforeDestroy() {
            this.destroy();
        },
    };
</script>



// WEBPACK FOOTER //
// vehi-industriepfad/assets/src/js/app/framework/components/ink-ripple/index.vue?7d95c7a8